import { useState, useEffect, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Button,
  Link,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import PendingActionsRoundedIcon from "@mui/icons-material/PendingActionsRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import { getAdminPendingBbps, resolvePendingBbps } from "../api";

const formatDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt)
    ? "—"
    : dt.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const formatAmount = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;

const STATUS_COLORS = {
  pending: "warning",
  processing: "info",
};

export default function AdminPendingBills() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState(null);
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolveNote, setResolveNote] = useState("");
  const [resolving, setResolving] = useState(false);
  const [dialogError, setDialogError] = useState(null);

  const fetchPending = useCallback(() => {
    setLoading(true);
    setError(null);
    getAdminPendingBbps()
      .then(({ data }) => setPayments(data.payments || []))
      .catch((e) =>
        setError(e.response?.data?.error || "Failed to load pending bills"),
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const openResolve = (payment) => {
    setResolveTarget(payment);
    setResolveNote("");
    setDialogError(null);
  };

  const closeResolve = () => {
    if (resolving) return;
    setResolveTarget(null);
    setResolveNote("");
    setDialogError(null);
  };

  const handleResolve = async () => {
    if (!resolveTarget) return;
    setResolving(true);
    setDialogError(null);
    try {
      await resolvePendingBbps(resolveTarget.id, resolveNote.trim());
      setPayments((prev) => prev.filter((p) => p.id !== resolveTarget.id));
      setResolveTarget(null);
      setResolveNote("");
      setSnack({ severity: "success", msg: "Bill marked resolved" });
    } catch (e) {
      setDialogError(e.response?.data?.error || "Failed to mark bill resolved");
    }
    setResolving(false);
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Pending / Unresolved Bills{" "}
            {!loading && payments.length > 0 ? `(${payments.length})` : ""}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            BBPS bill payments awaiting confirmation. Verify each one on the
            BillAvenue portal, then mark it resolved to clear it from the queue.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshRoundedIcon />}
          onClick={fetchPending}
          disabled={loading}
          sx={{ borderRadius: 2, textTransform: "none" }}
        >
          Refresh list
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ p: 2, overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : payments.length === 0 ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <PendingActionsRoundedIcon
              sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
            />
            <Typography color="text.secondary">
              No pending bills — all resolved 🎉
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Biller</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>BBPS Ref</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/admin/users/${p.userId}`}
                        underline="hover"
                        variant="body2"
                        fontWeight={600}
                      >
                        {p.userName || "—"}
                      </Link>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {p.email || p.mobile || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {p.billerName || "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.category || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {formatAmount(p.amount)}
                      </Typography>
                      {p.charge ? (
                        <Typography variant="caption" color="text.secondary">
                          +{formatAmount(p.charge)} charge
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={p.status || "—"}
                        color={STATUS_COLORS[p.status] || "default"}
                        sx={{ textTransform: "capitalize" }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                      {p.bbpsRefId || p.txnRefId || "—"}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>
                      {formatDate(p.createdAt)}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<DoneAllRoundedIcon />}
                        onClick={() => openResolve(p)}
                        sx={{ borderRadius: 2, textTransform: "none" }}
                      >
                        Mark resolved
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>

      <Dialog
        open={Boolean(resolveTarget)}
        onClose={closeResolve}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Mark bill resolved</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Resolving only removes this bill from the queue — it does NOT refund
            the customer. If the customer is owed a refund (the bill did not go
            through), first credit them via Adjust Wallet on their profile. If
            the bill actually succeeded, no refund is needed.
          </Alert>
          {resolveTarget && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>{resolveTarget.billerName || "—"}</strong>
                {" · "}
                {formatAmount(resolveTarget.amount)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {resolveTarget.userName || "—"}
              </Typography>
            </Box>
          )}
          <TextField
            label="Note (optional)"
            placeholder="Why / how was this resolved?"
            value={resolveNote}
            onChange={(e) => setResolveNote(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            disabled={resolving}
          />
          {dialogError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {dialogError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeResolve}
            disabled={resolving}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleResolve}
            disabled={resolving}
            startIcon={
              resolving ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <DoneAllRoundedIcon />
              )
            }
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            {resolving ? "Resolving…" : "Mark resolved"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={3000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack?.severity || "info"}
          onClose={() => setSnack(null)}
          sx={{ width: "100%" }}
        >
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
